import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Box, Typography } from '@mui/material';
import CMSConsumerCarouselBanner from '../../section/CMS/components/banner/CMSConsumerCarouselBanner';
import AccordionComponent from '../../components/shared/accordion/AccordionComponent';
import MailingListSignup from '../../components/shared/mailing/Mailing';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import MissingPage from '../../components/shared/errorMessages/MissingPage';
import { captureAnalytics } from '../../utilities/Analytics';
import { getCategoryPageData } from '../../section/CMS/api/cmsApi';
import { CMSPageData } from '../../section/CMS/types/CMSTypes';
import posthog from '../../utils/posthog';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TradingHour {
  day: string;
  time: string;
}

interface Store {
  title: string;
  customHeading?: string;
  holidayNotice?: string[];
  address: string;
  phone: string;
  email: string;
  tradingHours: TradingHour[];
  mapUrl: string;
  appointmentOnly?: boolean;
}

interface ProvinceGroup {
  title: string;   // province name — used as accordion title
  stores: Store[];
}

interface RegionGroup {
  title: string;          // region name e.g. "South Africa", "Other Countries"
  provinces: ProvinceGroup[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatTime = (timeString: string): string => {
  if (!timeString) return '';
  return timeString.split(':').slice(0, 2).join(':');
};

const transformOperatingHours = (operatingTimeslots: any[]): TradingHour[] => {
  if (!operatingTimeslots || !Array.isArray(operatingTimeslots)) {
    return [
      { day: 'Monday - Thursday', time: '08:00 - 17:00' },
      { day: 'Friday', time: '08:00 - 16:30' },
      { day: 'Saturday', time: '09:00 - 14:00' },
      { day: 'Sunday', time: 'Closed' },
      { day: 'Public Holidays', time: 'Closed' },
    ];
  }

  const hoursMap: Record<string, string> = {};
  operatingTimeslots.forEach(slot => {
    if (slot.opening_time && slot.closing_time) {
      hoursMap[slot.day_of_week_name] = `${formatTime(slot.opening_time)} - ${formatTime(slot.closing_time)}`;
    }
  });

  const tradingHours: TradingHour[] = [];
  const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  let currentGroup: string[] = [];
  let currentHours: string | null = null;

  for (let i = 0; i < weekdays.length; i++) {
    const day = weekdays[i];
    const hours = hoursMap[day];

    if (hours && hours === currentHours) {
      currentGroup.push(day);
    } else {
      if (currentGroup.length > 0) {
        tradingHours.push({
          day: currentGroup.length === 1
            ? currentGroup[0]
            : `${currentGroup[0]} - ${currentGroup[currentGroup.length - 1]}`,
          time: currentHours!,
        });
      }
      if (hours) {
        currentGroup = [day];
        currentHours = hours;
      } else {
        currentGroup = [];
        currentHours = null;
        tradingHours.push({ day, time: 'Closed' });
      }
    }
  }

  if (currentGroup.length > 0) {
    tradingHours.push({
      day: currentGroup.length === 1
        ? currentGroup[0]
        : `${currentGroup[0]} - ${currentGroup[currentGroup.length - 1]}`,
      time: currentHours!,
    });
  }

  tradingHours.push({ day: 'Saturday', time: hoursMap['Saturday'] || 'Closed' });
  tradingHours.push({ day: 'Sunday', time: hoursMap['Sunday'] || 'Closed' });
  tradingHours.push({
    day: 'Public Holidays',
    time: hoursMap['Public Holidays'] || hoursMap['Holiday'] || 'Closed',
  });

  return tradingHours;
};

/**
 * Transform a single raw store object into a Store display object.
 */
const transformSingleStore = (store: any): Store => {
  const addressParts = [
    store.address_complex_building,
    store.address_street,
    store.address_suburb,
    store.address_city,
  ].filter(Boolean);

  // Don't append zip for international locations
  const isInternational = store.address_country && store.address_country.toLowerCase() !== 'south africa';
  if (!isInternational && store.address_zip) {
    addressParts.push(store.address_zip);
  }

  const transformed: Store = {
    title: store.location_name || 'Store',
    customHeading: store.custom_heading || undefined,
    address: addressParts.join(', '),
    phone: store.location_phone || '',
    email: store.location_email || '',
    tradingHours: transformOperatingHours(store.operating_timeslots || []),
    mapUrl: store.address_maps_link || '',
    appointmentOnly: store.appointment_only === true,
  };

  if (store.location_description) {
    transformed.holidayNotice = [store.location_description];
  }

  return transformed;
};

/**
 * Province ordering for South Africa.
 * Other provinces / regions will be sorted alphabetically after these.
 */
const sortProvinces = (provinces: ProvinceGroup[]): ProvinceGroup[] => {
  return [...provinces].sort((a, b) => a.title.localeCompare(b.title));
};

/**
 * Transform cms store_locations (keyed by region, each region is an array of stores)
 * into RegionGroup[].
 *
 * The API returns:
 *   store_locations.active = {
 *     "South Africa": [ ...stores ],
 *     "Other Countries": [ ...stores ],
 *   }
 *
 * Within each region, stores are further grouped by address_province for the
 * province-level accordion.
 *
 * "South Africa" is always rendered first; remaining regions follow alphabetically.
 */
const transformStoreData = (storeLocations: any): RegionGroup[] => {
  if (!storeLocations?.active) return [];

  const active = storeLocations.active as Record<string, any[]>;
  const regionGroups: RegionGroup[] = [];

  for (const [regionName, stores] of Object.entries(active)) {
    if (!Array.isArray(stores) || stores.length === 0) continue;

    // Group stores within this region by province
    const provinceMap: Record<string, Store[]> = {};
    for (const store of stores) {
      // Only show stores flagged for display on the CMS store locator.
      // Only hide on an explicit false so stores stay visible if the field
      // is ever absent from the payload.
      if (store.cms_display_switch === false) continue;

      const province = store.address_province?.trim() || 'Other';
      if (!provinceMap[province]) provinceMap[province] = [];
      provinceMap[province].push(transformSingleStore(store));
    }

    const provinces = sortProvinces(
      Object.entries(provinceMap).map(([title, stores]) => ({ title, stores }))
    );

    regionGroups.push({ title: regionName, provinces });
  }

  // Always put "South Africa" first, then everything else alphabetically
  return regionGroups.sort((a, b) => {
    if (a.title === 'South Africa') return -1;
    if (b.title === 'South Africa') return 1;
    return a.title.localeCompare(b.title);
  });
};

// ─── Component ────────────────────────────────────────────────────────────────

const StoreLocatorPage = () => {
  const location = useLocation();

  // Each region section tracks its own expanded province accordion
  const [expandedByRegion, setExpandedByRegion] = useState<Record<string, string | false>>({});

  const {
    isError: cmsError,
    isLoading: cmsLoading,
    data: cmsResponse,
  } = useQuery(
    ['cms-stores'],
    () => getCategoryPageData('stores', 'live', 'stores'),
    { staleTime: 600000 }
  );

  const cmsData: CMSPageData | null = cmsResponse?.cms_page || null;
  const regionGroups: RegionGroup[] = cmsData ? transformStoreData(cmsData.store_locations) : [];

  const descriptionParagraphs = cmsData?.description
    ? cmsData.description.split('\n\n').map(p => p.trim()).filter(Boolean)
    : [];

  const makeHandleChange = (regionTitle: string) =>
    (panel: string) =>
      (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedByRegion(prev => ({
          ...prev,
          [regionTitle]: isExpanded ? panel : false,
        }));

        if (isExpanded) {
          posthog.capture('store_locator_province_expanded', {
            region: regionTitle,
            province: panel,
            source: 'consumer',
          });
        }
      };

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    captureAnalytics('pageview', location.pathname, 'Store Locator');
  }, [location.pathname]);

  if (cmsLoading) return <LoadingScreen />;
  if (cmsError) return <MissingPage />;

  return (
    <>
      {/* Banner */}
      {cmsData && (
        <CMSConsumerCarouselBanner
          bannerMediaOption={cmsData.banner_media_option || 'carousel'}
          bannerMediaCarousel={cmsData.banner_media_carousel || []}
          bannerMediaVideo={cmsData.banner_media_video || null}
          bannerMediaVideoLink={cmsData.banner_media_video_link || ''}
          bannerLinkCategoryId={cmsData.banner_link_category_id || null}
          bannerWidth={cmsData.banner_width || 2880}
          bannerHeight={cmsData.banner_height || 1050}
        />
      )}

      {/* Page header + description */}
      <Box sx={{ width: '70%', ml: '15%', mt: 10, mb: 4 }}>
        {cmsData?.stores_title && (
          <Typography
            variant='h3'
            mb={3}
            fontWeight={500}
            textTransform='none'
            sx={{ fontSize: { xs: '44px', lg: '56px' } }}
          >
            {cmsData.stores_title}
          </Typography>
        )}

        {descriptionParagraphs.map((paragraph, index) => (
          <Typography
            key={index}
            mt={index === 0 ? 0 : 2}
            fontSize={{ xs: '16px', lg: '20px' }}
            fontWeight={400}
            lineHeight='26px'
            textTransform='none'
          >
            {paragraph}
          </Typography>
        ))}
      </Box>

      {/* Region groups → Province accordions → Store cards */}
      <Box sx={{ width: '70%', ml: '15%', mb: '3rem' }}>

        {/* "Store Locator" page title sits just above the region groups */}
        <Typography
          variant='h3'
          mb={2}
          mt={8}
          fontWeight={500}
          textTransform='none'
          sx={{ fontSize: { xs: '44px', lg: '56px' } }}
        >
          {cmsData?.page_title || 'Store Locator'}
        </Typography>

        {regionGroups.map((region, regionIndex) => (
          <Box key={region.title} sx={{ mb: regionIndex < regionGroups.length - 1 ? 6 : 0 }}>
            {/* Region heading — 40px Perpetua 500 */}
            <Typography
              variant='h3'
              textTransform='none'
              sx={{
                fontSize: { xs: '32px', lg: '40px' },
                fontWeight: 500,
                mt: regionIndex === 0 ? 4 : 8,
                mb: 3,
              }}
            >
              {region.title}
            </Typography>

            {/* Province accordions within this region */}
            {region.provinces.map((province, provinceIndex) => (
              <AccordionComponent
                key={province.title}
                panelNumber={provinceIndex}
                expanded={expandedByRegion[region.title] ?? false}
                handleChange={makeHandleChange(region.title)}
                title={province.title}
                stores={province.stores}
                displayFAQs={false}
                isTop={0}
              />
            ))}
          </Box>
        ))}
      </Box>

      <MailingListSignup isColor={true} />
    </>
  );
};

export default StoreLocatorPage;
